"use client";

import React, { useMemo, useEffect, useState, useRef } from "react";
import { ModalData } from "@/app/(workspace)/_hooks/useModal";
import { useUploadProgress } from "@/stores/progress";

interface ProgressModalProps {
  data: ModalData;
  onClose: () => void;
}

export const ProgressModal = ({ data, onClose }: ProgressModalProps) => {
  if (!data.uploadKey) return null;

  const raw = useUploadProgress(
    (s) => s.progress[data.uploadKey as string] ?? 0
  );

  // 부드러운 애니메이션을 위한 로컬 상태
  const [displayPct, setDisplayPct] = useState(0);
  const startTimeRef = useRef<number>(Date.now());
  const MIN_DISPLAY_TIME = 2000; // 최소 2초 표시

  const pct = useMemo(() => {
    const n = Number.isFinite(raw) ? raw : 0;
    return Math.max(0, Math.min(100, n));
  }, [raw]);

  // 부드러운 진행률 애니메이션
  useEffect(() => {
    const diff = pct - displayPct;
    if (Math.abs(diff) < 0.1) {
      setDisplayPct(pct);
      return;
    }

    const step = diff > 0 ? Math.max(1, diff / 10) : Math.min(-1, diff / 10);
    const timer = setTimeout(() => {
      setDisplayPct((prev) => {
        const next = prev + step;
        return pct > prev ? Math.min(next, pct) : Math.max(next, pct);
      });
    }, 50); // 50ms마다 업데이트

    return () => clearTimeout(timer);
  }, [pct, displayPct]);

  // 100% 도달 시 최소 표시 시간 후 닫기
  useEffect(() => {
    if (pct >= 100) {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, MIN_DISPLAY_TIME - elapsed);

      const timer = setTimeout(() => {
        onClose();
      }, remaining);

      return () => clearTimeout(timer);
    }
  }, [pct, onClose]);

  const isDone = pct >= 100;

  return (
    <div className="bg-white dark:bg-zinc-900 p-8 select-none">
      <div className="flex flex-col items-center justify-center space-y-6">
        {/* 원형 프로그레스 */}
        <div className="relative w-32 h-32">
          <svg className="w-32 h-32 transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-zinc-200 dark:text-zinc-800"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              className={`${isDone
                  ? "text-emerald-500 dark:text-emerald-400"
                  : "text-emerald-600 dark:text-emerald-500"
                }`}
              style={{
                strokeDasharray: 352,
                strokeDashoffset: 352 - (352 * displayPct) / 100,
              }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-3xl font-bold text-zinc-900 dark:text-white tabular-nums">
              {displayPct.toFixed(0)}%
            </span>
          </div>
        </div>

        {/* 상태 메시지 */}
        <div className="text-center space-y-2">
          <p className="text-lg font-semibold text-zinc-900 dark:text-white">
            {isDone ? "Upload Complete!" : "Uploading..."}
          </p>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            {data.message}
          </p>
        </div>
      </div>
    </div>
  );
};
